import React from "react";
import { cn } from "@/lib/utils";

export const METRIC_CARD_ICON_COLOR = "#CAC19F";
export const METRIC_CARD_ICON_STROKE_WIDTH = 1.33;
export const METRIC_CARD_WIDTH = 240;

export type MetricCardTrend = "positive" | "negative" | "neutral";

export interface MetricCardProps {
  title: string;
  value: string;
  valueClassName?: string;
  delta?: string;
  description?: string;
  trend?: MetricCardTrend;
  icon?: React.ReactNode;
  iconColor?: string;
  iconSize?: number;
  width?: "fixed" | "fill";
  className?: string;
}

const cardShadow =
  "shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.1),0px_1px_3px_0px_rgba(0,0,0,0.1)]";

function resolveTrend(
  trend: MetricCardTrend | undefined,
  delta: string | undefined,
): MetricCardTrend {
  if (trend) {
    return trend;
  }

  if (!delta) {
    return "neutral";
  }

  const trimmed = delta.trim();
  if (trimmed.startsWith("+")) {
    return "positive";
  }
  if (trimmed.startsWith("-")) {
    return "negative";
  }

  return "neutral";
}

const trendHighlightClassName: Record<MetricCardTrend, string> = {
  positive: "text-emerald-600",
  negative: "text-red-600",
  neutral: "text-zinc-900",
};

function parseDelta(delta: string): { highlight: string; rest: string } {
  const trimmed = delta.trim();
  if (!trimmed.startsWith("+") && !trimmed.startsWith("-")) {
    return { highlight: trimmed, rest: "" };
  }

  const percentEnd = trimmed.indexOf("%");
  if (percentEnd === -1) {
    const spaceIndex = trimmed.indexOf(" ");
    if (spaceIndex === -1) {
      return { highlight: trimmed, rest: "" };
    }
    return {
      highlight: trimmed.slice(0, spaceIndex),
      rest: trimmed.slice(spaceIndex),
    };
  }

  const highlight = trimmed.slice(0, percentEnd + 1);
  const rest = trimmed.slice(percentEnd + 1).trim();
  return {
    highlight,
    rest: rest ? ` ${rest}` : "",
  };
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  valueClassName,
  delta,
  description,
  trend,
  icon,
  iconColor = METRIC_CARD_ICON_COLOR,
  iconSize = 16,
  width = "fixed",
  className,
}) => {
  const resolvedTrend = resolveTrend(trend, delta);
  const deltaParts = delta ? parseDelta(delta) : null;

  return (
    <article
      className={cn(
        "flex min-h-[124px] flex-col gap-2 rounded-xl border border-[#E5E5E5] bg-white p-6 font-sans",
        width === "fixed" ? "max-w-full shrink-0" : "w-full",
        cardShadow,
        className,
      )}
      style={width === "fixed" ? { width: METRIC_CARD_WIDTH } : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="shrink-0 text-sm leading-normal font-medium text-[#171717]">
          {title}
        </h3>
        {icon ? (
          <span
            className="flex shrink-0 items-center justify-center"
            style={{ color: iconColor, width: iconSize, height: iconSize }}
          >
            {React.isValidElement(icon)
              ? React.cloneElement(
                  icon as React.ReactElement<{
                    className?: string;
                    strokeWidth?: number;
                    style?: React.CSSProperties;
                  }>,
                  {
                    className: cn(
                      "shrink-0",
                      (icon as React.ReactElement<{ className?: string }>).props
                        .className,
                    ),
                    style: { width: iconSize, height: iconSize },
                    strokeWidth: METRIC_CARD_ICON_STROKE_WIDTH,
                  },
                )
              : icon}
          </span>
        ) : null}
      </div>

      <p
        className={cn(
          "text-[28px] leading-none font-semibold tracking-tight text-[#171717]",
          valueClassName,
        )}
      >
        {value}
      </p>

      {deltaParts ? (
        <p className="text-xs leading-normal">
          <span
            className={cn(
              "font-medium",
              trendHighlightClassName[resolvedTrend],
            )}
          >
            {deltaParts.highlight}
          </span>
          {deltaParts.rest ? (
            <span className="text-muted-foreground">{deltaParts.rest}</span>
          ) : null}
        </p>
      ) : null}

      {description ? (
        <p className="text-muted-foreground text-xs leading-normal break-words">
          {description}
        </p>
      ) : null}
    </article>
  );
};
